import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { LogoBicolor } from '../components/LogoBicolor';
import { Mail, Lock, User, FileText, Ticket, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import './landing-design.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared input style — matches the login form
const inputStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-glass-border)',
  color: 'var(--color-text-primary)',
  height: 44,
};

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    crefito: '',
    promo_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [promoState, setPromoState] = useState({ status: 'idle', message: '' });
  const debounceRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planIntent = searchParams.get('plan');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'promo_code') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = value.trim();
      if (!trimmed) {
        setPromoState({ status: 'idle', message: '' });
        return;
      }
      setPromoState({ status: 'checking', message: 'Verificando código...' });
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await axios.post(`${API_URL}/promo-codes/validate`, { code: trimmed });
          if (res.data?.valid) {
            setPromoState({ status: 'valid', message: res.data.message });
          } else {
            setPromoState({ status: 'invalid', message: res.data.message || 'Código inválido' });
          }
        } catch (err) {
          setPromoState({ status: 'invalid', message: 'Não foi possível validar o código' });
        }
      }, 450);
    }
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.promo_code.trim() && promoState.status === 'invalid') {
      toast.error('Remova ou corrija o código promocional antes de continuar');
      return;
    }

    setLoading(true);
    try {
      await register(
        formData.email,
        formData.password,
        formData.full_name,
        formData.crefito,
        formData.promo_code.trim() ? formData.promo_code.trim().toUpperCase() : null
      );
      if (promoState.status === 'valid') {
        toast.success('🎉 Conta criada! Seu plano Solo gratuito está ativo.');
      } else {
        toast.success('Conta criada com sucesso!');
      }
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const promoIndicator = () => {
    if (promoState.status === 'checking') {
      return <Loader2 className="h-5 w-5 animate-spin lp-text-muted" />;
    }
    if (promoState.status === 'valid') {
      return <CheckCircle2 className="h-5 w-5" style={{ color: '#10b981' }} />;
    }
    if (promoState.status === 'invalid') {
      return <XCircle className="h-5 w-5" style={{ color: '#ef4444' }} />;
    }
    return null;
  };

  return (
    <div className="akali-landing min-h-screen relative flex items-center justify-center px-4 py-12">
      <div className="lp-hero-glow" />

      <div className="relative z-10 max-w-md w-full">
        <div className="flex items-center justify-center mb-8">
          <LogoBicolor size={24} symbolSize={36} />
        </div>

        <p
          className="lp-mono lp-text-muted text-center mb-8"
          style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.15em' }}
          data-testid="brand-tagline"
        >
          Documentação clínica inteligente
        </p>

        <div className="lp-card p-8">
          <h1 className="lp-display lp-text-primary text-center mb-1" style={{ fontSize: 28, fontWeight: 600 }}>
            Criar conta
          </h1>
          <p className="lp-body lp-text-secondary text-center mb-8" style={{ fontSize: 15 }}>
            Comece grátis com 15 consultas
          </p>

          {planIntent === 'solo' && (
            <div
              className="flex items-center justify-center gap-2 mb-6 px-3 py-2 rounded-lg"
              style={{ background: 'rgba(241, 136, 89, 0.08)', border: '1px solid rgba(241, 136, 89, 0.30)' }}
              data-testid="register-plan-intent"
            >
              <CheckCircle2 className="h-4 w-4 lp-text-accent" />
              <span className="lp-body lp-text-secondary" style={{ fontSize: 13 }}>
                Plano Solo selecionado — você poderá assiná-lo após criar a conta
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>Nome Completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="Dr. João Silva"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="pl-10 lp-body"
                  style={inputStyle}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            {/* CREFITO */}
            <div className="space-y-2">
              <Label htmlFor="crefito" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>CREFITO (opcional)</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                <Input
                  id="crefito"
                  name="crefito"
                  type="text"
                  placeholder="CREFITO-3/123456-F"
                  value={formData.crefito}
                  onChange={handleChange}
                  className="pl-10 lp-body"
                  style={inputStyle}
                  data-testid="register-crefito-input"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 lp-body"
                  style={inputStyle}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            {/* Senha */}
            <div className="space-y-2">
              <Label htmlFor="password" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>Senha</Label>
              <PasswordInput
                id="password"
                name="password"
                leftIcon={Lock}
                iconColor="text-[color:var(--color-text-muted)]"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="lp-body"
                style={inputStyle}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="register-password-input"
              />
            </div>

            {/* Promo code */}
            <div className="space-y-2">
              <Label htmlFor="promo_code" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>
                Tem um código de acesso? (opcional)
              </Label>
              <div className="relative">
                <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                <Input
                  id="promo_code"
                  name="promo_code"
                  type="text"
                  placeholder="Ex.: AKALI30"
                  value={formData.promo_code}
                  onChange={handleChange}
                  className="pl-10 pr-10 uppercase tracking-wider lp-body"
                  style={inputStyle}
                  data-testid="register-promo-input"
                  autoComplete="off"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {promoIndicator()}
                </div>
              </div>
              {promoState.status === 'valid' && (
                <p className="lp-body font-medium" style={{ fontSize: 13, color: '#10b981' }} data-testid="promo-success-msg">
                  {promoState.message}
                </p>
              )}
              {promoState.status === 'invalid' && (
                <p className="lp-body" style={{ fontSize: 13, color: '#ef4444' }} data-testid="promo-error-msg">
                  {promoState.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="lp-btn-primary w-full"
              disabled={loading}
              data-testid="register-submit-btn"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Criando conta...</>
              ) : (
                'Criar conta'
              )}
            </button>
          </form>

          <div
            className="mt-6 text-center lp-body lp-text-muted"
            style={{ fontSize: 14, borderTop: '1px solid var(--color-glass-border)', paddingTop: 24 }}
          >
            Já tem uma conta?{' '}
            <Link
              to="/login"
              className="lp-text-accent hover:underline"
              style={{ fontWeight: 500 }}
            >
              Fazer login
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="lp-mono lp-text-muted hover:lp-text-primary transition-colors"
            style={{ fontSize: 12 }}
          >
            ← Voltar para a home
          </Link>
        </div>
      </div>
    </div>
  );
}
